import { useEffect } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, Close, Upload5 } from 'blaze-ui/icons'
import { ImageThumbnail } from 'components/ImagePickerModal/ImageThumbnail/ImageThumbnail'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import { useMediaSelection, mediaFileToSelectedImage } from '../../hooks'
import Styles from '../../VisualStylesForExistingUsersModal.module.scss'

interface MobileMediaSelectionStepProps {
  selectedMediaFileId: number | null
  onSelectMediaFile: (id: number) => void
  onNext: () => void
  onBack: () => void
  onClose: () => void
  brandKitId: number
}

export const MobileMediaSelectionStep = ({
  selectedMediaFileId,
  onSelectMediaFile,
  onNext,
  onBack,
  onClose,
  brandKitId,
}: MobileMediaSelectionStepProps) => {
  const { mediaFiles, isLoading, isUploading, uploadProgress, handleSelectMediaFile, handleUpload } = useMediaSelection(
    {
      brandKitId,
      selectedMediaFileId,
      onSelectMediaFile,
    },
  )

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-media-selection-step-mobile',
    })
  }, [])

  const handleNext = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-media-selection-step-mobile',
      selected_media_file_id: selectedMediaFileId ?? undefined,
    })
    onNext()
  }

  return (
    <div className={Styles.mobileStepContainer}>
      <div className={Styles.mobileStepHeader}>
        <Button size="sm" variant="tertiary" onPress={onBack}>
          <ArrowLeft size={20} />
        </Button>
        <Button size="sm" variant="tertiary" onPress={onClose}>
          <Close size={20} />
        </Button>
      </div>

      <div className={Styles.mobileStepContent}>
        <div className={Styles.mobileMediaSelectionContent}>
          <div className={Styles.mobileMediaSelectionHeader}>
            <Heading level={1}>Pick a photo to preview your style</Heading>
            <Text color="var(--dark-40)" variant="primary">
              We'll create a cinematic look for all your content based on this
            </Text>
          </div>

          <Heading level={4}>Choose a photo:</Heading>

          <div className={Styles.mobileMediaGrid}>
            {isLoading &&
              Array.from({ length: 6 }).map((_, i) => (
                <div key={`skeleton-${i}`} className={Styles.mobileMediaGridItem}>
                  <ImageThumbnail image={{ url: '' }} forceLoading />
                </div>
              ))}
            {!isLoading &&
              mediaFiles.slice(0, 8).map((file) => (
                <div key={file.media_file_id} className={Styles.mobileMediaGridItem}>
                  <ImageThumbnail
                    image={mediaFileToSelectedImage(file)}
                    isSelected={selectedMediaFileId === file.media_file_id}
                    onToggleSelect={handleSelectMediaFile}
                  />
                </div>
              ))}
            {!isLoading && (
              <FileTrigger
                isDisabled={isUploading}
                accept={ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES}
                allowsMultiple
                onSelect={handleUpload}
              >
                <button className={Styles.mobileUploadButton} disabled={isUploading}>
                  {isUploading ? (
                    <Text variant="secondary" color="var(--dark-60)">
                      {uploadProgress.toFixed(0)}%
                    </Text>
                  ) : (
                    <>
                      <Upload5 size={20} color="var(--dark-60)" />
                      <Text variant="metadata" color="var(--dark-60)">
                        Upload New
                      </Text>
                    </>
                  )}
                </button>
              </FileTrigger>
            )}
          </div>
        </div>
      </div>

      <div className={Styles.mobileFooter}>
        <Button
          variant="primary"
          size="lg"
          onPress={handleNext}
          isDisabled={!selectedMediaFileId || isUploading}
          className={Styles.mobileFullWidthButton}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}
